import axios from 'axios'

// const API_URL = 'https://app-testing-api.aventra.ru'
// При сборке без VUE_APP_API_URL используется относительный путь /api (для production на том же домене)
const API_URL = process.env.VUE_APP_API_URL || '/api'

const api = axios.create()

// В LMS авторизация только через JWT, дополнительных заголовков не требуется

let authToken = process.env.VUE_APP_AUTH_TOKEN || process.env.VUE_APP_JWT_TOKEN || ''
try {
  const t = localStorage.getItem('jwt_token') || ''
  if (t) authToken = t
} catch (e) { void e }
const setAuthToken = (token) => {
  authToken = token || ''
  try {
    if (token) localStorage.setItem('jwt_token', token)
    else localStorage.removeItem('jwt_token')
  } catch (e) { void e }
}

api.interceptors.request.use((config) => {
  let token = authToken
  try {
    if (!token) token = localStorage.getItem('jwt_token') || ''
  } catch (e) { void e }
  const isFormData = config.data instanceof FormData
  config.headers = {
    ...config.headers,
    accept: '*/*',
    ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  return config
})

// Объект с методами API
const apiMethods = {
  // Auth
  async bitrixSignIn(payload) {
    const { data } = await api.post(`${API_URL}/auth/bitrix/sign-in`, payload)
    return data
  },

  async getBitrixSettings() {
    const { data } = await api.get(`${API_URL}/bitrix/settings`)
    return data
  },

  async getBitrixUsers(employeesOnly = false) {
    const { data } = await api.get(`${API_URL}/bitrix/users`, employeesOnly ? { params: { employeesOnly: true } } : undefined)
    return Array.isArray(data?.users) ? data.users : []
  },

  async getBitrixDepartments() {
    const { data } = await api.get(`${API_URL}/bitrix/departments`)
    return Array.isArray(data?.departments) ? data.departments : []
  },

  async getBitrixDepartmentUsers(deptId) {
    const { data } = await api.get(`${API_URL}/bitrix/departments/${deptId}/users`)
    return Array.isArray(data?.users) ? data.users : []
  },

  async getBitrixDepartmentsUsersBulk() {
    const { data } = await api.get(`${API_URL}/bitrix/departments/users/bulk`)
    const map = data && typeof data.map === 'object' && data.map !== null ? data.map : {}
    return map
  },

  async retryBitrixInstall() {
    const { data } = await api.post(`${API_URL}/bitrix/install/retry`)
    return data
  },

  // Portal
  async portal(params) {
    const { data } = await api.post(`${API_URL}/portal`, params)
    return data
  },

  async getPortal() {
    const { data } = await api.get(`${API_URL}/portal`)
    return data
  },

  async updatePortal(id, params) {
    return await api.put(`${API_URL}/portal/${id}`, params)
  },

  // Import portal users in chunks to avoid oversized payloads
  async importPortalUsersChunk(params) {
    const { data } = await api.post(`${API_URL}/portal/users/import`, params)
    return data
  },

  // Courses
  getCourses(assignedOnly = false) {
    const params = {}
    if (assignedOnly) params.assignedOnly = true
    return api.get(`${API_URL}/course`, { params })
  },

  async getCourse(courseId) {
    const { data } = await api.get(`${API_URL}/course/${courseId}`)
    return data
  },

  async getCourseStatistics(courseId) {
    const { data } = await api.get(`${API_URL}/course/${courseId}/statistics`)
    return data
  },

  // Tests
  async getTest(courseId, testId) {
    const { data } = await api.get(
      `${API_URL}/course/${courseId}/test/${testId}`
    )
    return data
  },

  async startTest(courseId, testId) {
    const { data } = await api.post(
      `${API_URL}/course/${courseId}/test/${testId}/start`
    )
    return data
  },

  async answerQuestion(courseId, testId, params) {
    const { data } = await api.post(
      `${API_URL}/course/${courseId}/test/${testId}/answer`,
      params
    )
    return data
  },

  async completeTest(courseId, testId, payload) {
    // Теперь метод принимает payload и отправляет его в теле запроса
    const { data } = await api.post(
      `${API_URL}/course/${courseId}/test/${testId}/complete`,
      payload
    )
    return data
  },
  
  async getAttemptDetails(courseId, testId, attemptId) {
    const { data } = await api.get(
      `${API_URL}/course/${courseId}/test/${testId}/attempt/${attemptId}`
    )
    return data
  },

  // Statistics
  async getCoursesStatistics() {
    const { data } = await api.get(`${API_URL}/course/statistics`)
    return data
  },

  async getUsersStatistics() {
    const { data } = await api.get(`${API_URL}/course/users/statistics`)
    return data
  },

  async getEventsStatistics(page = 1, limit = 25) {
    const { data } = await api.get(`${API_URL}/course/events`, {
      params: { page, limit }
    })
    return data
  },

  async getTestLeaderboard(courseId, testId, sortBy = 'score') {
    const { data } = await api.get(
      `${API_URL}/course/${courseId}/test/${testId}/leaderboard?sortBy=${sortBy}`
    )
    return data
  },

  // FOR ADMIN

  // COURSE
  async createCourse(params) {
    const { data } = await api.post(`${API_URL}/course`, params)
    return data
  },

  async updateCourse(courseId, data) {
    return api.patch(`${API_URL}/course/${courseId}`, data)
  },

  // LESSON
  async createLesson(courseId, params) {
    return await api.post(`${API_URL}/course/${courseId}/lesson`, params)
  },

  async updateLesson(courseId, lessonId, params) {
    return await api.patch(
      `${API_URL}/course/${courseId}/lesson/${lessonId}`,
      params
    )
  },

  async reorderLessons(courseId, lessons) {
    return await api.patch(
      `${API_URL}/course/${courseId}/lesson/reorder`,
      { lessons }
    )
  },

  async deleteLesson(courseId, lessonId) {
    return await api.delete(`${API_URL}/course/${courseId}/lesson/${lessonId}`)
  },

  // TEST
  async createTest(courseId, params) {
    return await api.post(`${API_URL}/course/${courseId}/test`, params)
  },

  async updateTest(courseId, testId, params) {
    return await api.patch(
      `${API_URL}/course/${courseId}/test/${testId}`,
      params
    )
  },

  async deleteTest(courseId, testId) {
    return await api.delete(`${API_URL}/course/${courseId}/test/${testId}`)
  },

  async createQuestion(courseId, testId, params) {
    return await api.post(
      `${API_URL}/course/${courseId}/test/${testId}/question`,
      params
    )
  },

  async updateQuestion(courseId, testId, questionId, params) {
    return await api.patch(
      `${API_URL}/course/${courseId}/test/${testId}/question/${questionId}`,
      params
    )
  },

  // Удаление курса
  async deleteCourse(courseId) {
    return await api.delete(`${API_URL}/course/${courseId}`)
  },

  // Удаление вопроса в тесте
  async deleteQuestion(courseId, testId, questionId) {
    return await api.delete(
      `${API_URL}/course/${courseId}/test/${testId}/question/${questionId}`
    )
  },

  // Отчеты
  async downloadAttemptsReport() {
    return await api.get(`${API_URL}/report/attempts`, {
      responseType: 'blob',
    })
  },

  async downloadAttemptsReportByCourse(courseId) {
    const params = {}
    if (courseId) params.courseId = courseId
    return await api.get(`${API_URL}/report/attempts`, {
      params,
      responseType: 'blob',
    })
  },

  async downloadQuestionsReport() {
    return await api.get(`${API_URL}/report/questions`, {
      responseType: 'blob',
    })
  },

  async downloadQuestionsReportByCourse(courseId) {
    const params = {}
    if (courseId) params.courseId = courseId
    return await api.get(`${API_URL}/report/questions`, {
      params,
      responseType: 'blob',
    })
  },

  async assignCourse(courseId, payload) {
    return await api.post(`${API_URL}/course/${courseId}/assign`, payload)
  },

  async listAssignments(courseId, params = {}) {
    const { data } = await api.get(`${API_URL}/course/${courseId}/assignments`, { params })
    return data
  },

  async updateAssignment(courseId, assignmentId, payload) {
    const { data } = await api.patch(`${API_URL}/course/${courseId}/assignments/${assignmentId}`, payload)
    return data
  },

  // Снимает назначение у сотрудников целиком (все активные строки) и закрывает задачи Bitrix.
  async cancelAssignments(courseId, userIds) {
    const { data } = await api.post(`${API_URL}/course/${courseId}/assignments/cancel`, { userIds })
    return data
  },

  async repeatAssignments(courseId, payload) {
    const { data } = await api.post(`${API_URL}/course/${courseId}/assign/repeat`, payload)
    return data
  },

  // Upload — использует XMLHttpRequest для поддержки onUploadProgress
  async uploadFile(formData, onUploadProgress) {
    let token = authToken
    try {
      if (!token) token = localStorage.getItem('jwt_token') || ''
    } catch (e) { void e }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', `${API_URL}/upload`, true)

      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      }

      xhr.upload.addEventListener('progress', (e) => {
        if (onUploadProgress) onUploadProgress(e)
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText))
          } catch {
            resolve(xhr.responseText)
          }
        } else {
          const error = new Error(`Upload failed: ${xhr.status}`)
          error.response = {
            status: xhr.status,
            statusText: xhr.statusText,
            data: (() => { try { return JSON.parse(xhr.responseText) } catch { return xhr.responseText } })(),
            headers: {},
          }
          reject(error)
        }
      })

      xhr.addEventListener('error', () => {
        const error = new Error('Network error during upload')
        error.request = xhr
        reject(error)
      })

      xhr.addEventListener('timeout', () => {
        const error = new Error('Upload timeout')
        error.code = 'ECONNABORTED'
        error.request = xhr
        reject(error)
      })

      xhr.send(formData)
    })
  },
}

// Экспортируем все вместе
export default {
  ...apiMethods,
  setAuthToken,
}
